import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import {
  accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount,
} from '../lib/accountStorage';
import { changedFields, createDocSync, type DocBase, type DocSync } from '../lib/syncDoc';
import { supabaseBlobTransport } from '../lib/syncBlobTransport';
import { loadedFor, refreshBlobOnFocus } from '../lib/blobFocusRefresh';

/**
 * Life Balance Tuning — "loadouts" (Phase 1: the loadout manager).
 *
 * A loadout is a named target time-allocation across the 7 life domains for a
 * given "mode" (Work Week, Weekend Flow, …). Only one loadout is active at a
 * time; later phases compare day-template schedules against the active loadout.
 *
 * The whole { activeLoadoutId, loadouts } blob lives in `user_loadouts.data`,
 * with a localStorage cache for offline.
 *
 * ⚠️⚠️ Writes go through syncDoc (lib/syncDoc.ts), NOT a whole-row upsert: the
 * old upsert wrote this tab's copy of every loadout, so a stale tab brought back
 * loadouts deleted elsewhere — and `applyDayRule` writes by itself on load. Now
 * a write re-reads the row and applies only what THIS tab changed (loadouts by
 * id; a delete beats an edit).
 */

/** Canonical domain ids the allocations are keyed by (matches constants/categories). */
export const LOADOUT_DOMAINS = [
  'personal',
  'social',
  'occupational',
  'avocational',
  'intellectual',
  'ecological',
  'existential',
] as const;

export type LoadoutDomain = (typeof LOADOUT_DOMAINS)[number];
export type LoadoutAllocations = Record<LoadoutDomain, number>;

export interface Loadout {
  id: string;
  name: string;
  /** Hours dedicated to sleep (4–12). Waking hours = 24 − sleepHours. */
  sleepHours: number;
  /** Target hours per domain. Sum (+ commute) must be ≤ waking hours. */
  allocations: LoadoutAllocations;
  /** Target commute/transit hours per day (counts against waking, separate from the 7 domains). */
  commuteHours?: number;
  /** Weekdays (0=Sun … 6=Sat) this loadout auto-activates on. Empty = manual only. */
  days?: number[];
}

export const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;
export const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export const emptyAllocations = (): LoadoutAllocations =>
  LOADOUT_DOMAINS.reduce((acc, d) => { acc[d] = 0; return acc; }, {} as LoadoutAllocations);

export const wakingHours = (sleepHours: number): number =>
  Math.round((24 - sleepHours) * 100) / 100;

export const allocatedHours = (allocations: LoadoutAllocations): number =>
  LOADOUT_DOMAINS.reduce((sum, d) => sum + (allocations[d] || 0), 0);

/** Single source of truth for rendering an hour value (rounds to 1 decimal, e.g. "7.5h"). */
export const formatHours = (h: number): string => `${Math.round(h * 10) / 10}h`;

// ─── Level-gated loadout capacity ───────────────────────────────────────────
// Users start with a small number of loadout slots; more unlock as they level up.
// Gate on the ABSOLUTE level (stats.totalLevel), not the per-tier level, so capacity
// never drops at a tier boundary.
export const LOADOUT_BASE_CAPACITY = 2;
export const LOADOUT_LEVELS_PER_SLOT = 5;
export const LOADOUT_MAX_CAPACITY = 8;

/** How many loadouts a user at `totalLevel` may keep (2 base, +1 every 5 levels, max 8). */
export const loadoutCapacity = (totalLevel: number): number =>
  Math.min(
    LOADOUT_MAX_CAPACITY,
    LOADOUT_BASE_CAPACITY + Math.floor(Math.max(0, totalLevel || 0) / LOADOUT_LEVELS_PER_SLOT)
  );

/** The level at which the next slot unlocks, or null if already at max capacity. */
export const nextSlotLevel = (totalLevel: number): number | null => {
  if (loadoutCapacity(totalLevel) >= LOADOUT_MAX_CAPACITY) return null;
  return (Math.floor(Math.max(0, totalLevel || 0) / LOADOUT_LEVELS_PER_SLOT) + 1) * LOADOUT_LEVELS_PER_SLOT;
};

function genId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  } catch { /* fall through */ }
  return `lo_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

interface LifeBalanceState {
  loadouts: Loadout[];
  activeLoadoutId: string | null;
  initializedUserId: string | null;
  cachedUserId: string | null;
  /** The last cloud copy this tab agreed with — see lib/syncDoc.ts. */
  syncBase: DocBase<LoadoutDoc> | null;
  initialize: () => Promise<void>;
  /** Create a loadout; returns its id (becomes active if it's the first one). */
  addLoadout: (data: Omit<Loadout, 'id'>) => string;
  updateLoadout: (id: string, patch: Partial<Omit<Loadout, 'id'>>) => void;
  deleteLoadout: (id: string) => void;
  setActiveLoadout: (id: string) => void;
  getActiveLoadout: () => Loadout | null;
  /** Auto-activate the loadout whose day-rule matches today (if any). Returns true if it switched. */
  applyDayRule: () => boolean;
}

const LOADOUTS_TABLE = 'user_loadouts';

type LoadoutDoc = { activeLoadoutId: string | null; loadouts: Loadout[] };

function pickData(s: Pick<LifeBalanceState, 'activeLoadoutId' | 'loadouts'>): LoadoutDoc {
  return { activeLoadoutId: s.activeLoadoutId, loadouts: s.loadouts };
}

/** The same reads the load always did. */
function hydrateLoadouts(data: unknown): LoadoutDoc {
  const d = (data && typeof data === 'object' && !Array.isArray(data) ? data : {}) as Record<string, unknown>;
  const loadouts = Array.isArray(d.loadouts) ? (d.loadouts as Loadout[]) : [];
  return { activeLoadoutId: (d.activeLoadoutId as string | null | undefined) ?? loadouts[0]?.id ?? null, loadouts };
}

// ⚠️ An active id whose loadout was deleted on another device reads as "none
// active" (`getActiveLoadout` → null) until the next pick or day rule.
const loadoutSync: DocSync<LoadoutDoc> = createDocSync<LoadoutDoc>({
  transport: supabaseBlobTransport(LOADOUTS_TABLE),
  owner: () => useLifeBalanceStore.getState().cachedUserId,
  rules: { loadouts: { records: 'id' } },
  hydrate: hydrateLoadouts,
  read: () => pickData(useLifeBalanceStore.getState()),
  readBase: () => useLifeBalanceStore.getState().syncBase,
  adopt: (doc, base) => useLifeBalanceStore.setState((state: LifeBalanceState) => ({
    ...changedFields(pickData(state), doc),
    syncBase: base,
  })),
  onError: (error) => console.error('Failed to sync loadouts to Supabase:', error),
});

/** Await the pending loadouts write (tests). */
export function flushLoadoutSync(): Promise<void> {
  return loadoutSync.flush();
}

// A tab that comes back shows loadouts changed elsewhere (lib/blobFocusRefresh.ts).
refreshBlobOnFocus(LOADOUTS_TABLE, () => loadoutSync.refresh(loadedFor(() => useLifeBalanceStore.getState().initializedUserId)));

export const useLifeBalanceStore = create<LifeBalanceState>()(
  persist<LifeBalanceState, [], [], any>(
    (rawSet, get) => {
      // ⚠️ The first edit of a tab whose load never finished records its base
      // (see the same wrapper in userStore).
      const set: typeof rawSet = (...args: Parameters<typeof rawSet>) => {
        if (get().syncBase === null) rawSet({ syncBase: loadoutSync.baseOf(pickData(get())) });
        return rawSet(...args);
      };
      const sync = () => loadoutSync.schedule();

      return {
        loadouts: [],
        activeLoadoutId: null,
        initializedUserId: null,
        cachedUserId: null,
        syncBase: null,

        // Load-on-login, keyed by user id. The cloud wins for everything this tab
        // has not changed since its base; a cache that is another account's is
        // replaced outright.
        initialize: () => dedupeInit('user_loadouts', () => loadoutSync.exclusive(async () => {
          const at = accountGeneration();
          try {
            const { user } = await syncManager.safeAuthCheck();
            if (!user) return;
            if (get().initializedUserId === user.id) return;
            // Cache-first boot: while this user's local cache is fresh, skip the
            // cloud read entirely (write-through keeps cloud current meanwhile).
            if (get().cachedUserId === user.id && isCloudFresh('user_loadouts', user.id)) {
              rawSet((state) => ({
                initializedUserId: user.id,
                syncBase: state.syncBase ?? loadoutSync.baseOf(pickData(state)),
              }));
              return;
            }

            const { data, error } = await supabase.from(LOADOUTS_TABLE).select('*');
            // An answer for an account that has since signed out is not this copy's.
            if (at !== accountGeneration()) return;
            if (error) throw error;

            const row = (data ?? [])[0];
            const cloudData = row && row.data && Array.isArray(row.data.loadouts) ? row.data : null;
            const state = get();
            const cacheIsOurs = state.cachedUserId === null || state.cachedUserId === user.id;
            let unsaved = false;
            if (cacheIsOurs) unsaved = loadoutSync.reconcile(cloudData);
            else loadoutSync.replace(cloudData);
            loadoutSync.noteLoaded(row?.updated_at ?? null);
            rawSet({ initializedUserId: user.id, cachedUserId: user.id });
            if (unsaved) sync();
            // Auto-activate today's scheduled loadout, if any rule matches.
            get().applyDayRule();
            markCloudFresh('user_loadouts', user.id);
          } catch (error) {
            console.error('Failed to initialize loadouts from Supabase:', error);
          }
        })),

        addLoadout: (data) => {
          const id = genId();
          set((state) => ({
            loadouts: [...state.loadouts, { id, ...data }],
            // First loadout auto-activates.
            activeLoadoutId: state.activeLoadoutId ?? id,
          }));
          sync();
          return id;
        },

        updateLoadout: (id, patch) => {
          set((state) => ({
            loadouts: state.loadouts.map((l) => (l.id === id ? { ...l, ...patch } : l)),
          }));
          sync();
        },

        deleteLoadout: (id) => {
          set((state) => {
            const loadouts = state.loadouts.filter((l) => l.id !== id);
            const activeLoadoutId =
              state.activeLoadoutId === id ? (loadouts[0]?.id ?? null) : state.activeLoadoutId;
            return { loadouts, activeLoadoutId };
          });
          sync();
        },

        setActiveLoadout: (id) => {
          set({ activeLoadoutId: id });
          sync();
        },

        getActiveLoadout: () => {
          const { loadouts, activeLoadoutId } = get();
          return loadouts.find((l) => l.id === activeLoadoutId) ?? null;
        },

        applyDayRule: () => {
          const { loadouts, activeLoadoutId } = get();
          const weekday = new Date().getDay();
          const match = loadouts.find((l) => Array.isArray(l.days) && l.days.includes(weekday));
          if (match && match.id !== activeLoadoutId) {
            set({ activeLoadoutId: match.id });
            sync();
            return true;
          }
          return false;
        },
      };
    },
    {
      name: 'life-balance-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 1,
      partialize: (state) => ({
        loadouts: state.loadouts,
        activeLoadoutId: state.activeLoadoutId,
        cachedUserId: state.cachedUserId,
        syncBase: state.syncBase,
      } as any),
    }
  )
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: an account's edits whose
   write had not landed lived only until the next account's load replaced the copy, and the next
   account's edits made before its own load were merged into the last one's row by ITS next load. */
scopeToAccount(useLifeBalanceStore);
